import { createApp } from 'vue'

import { VueQueryPlugin } from '@tanstack/vue-query'
import Keycloak from 'keycloak-js'

import { schedulePwaClientInit } from '@/lib/pwa/initPwaClient'
import { trackStandalonePwaInstall } from '@/lib/pwa/pwaAnalytics'
import { registerChunkErrorRecovery } from '@/lib/pwa/recoverFromChunkError'
import { queryClient } from '@/lib/queryClient'
import router, { setPostAuthRedirect } from '@/router'
import store from '@/store'

import { AuthApi } from './apiClient'
import { SESSION_EXPIRED_USER_MESSAGE } from './apiClient/feErrorCodes'
import App from './App.vue'
import { useAuthTokens } from './composables/useAuthTokens'
import { initEmbedAuthBridge } from './lib/embed/embedAuthBridge'
import createLogger from './lib/logger'
import {
  isMaintenanceMode,
  startMaintenanceModeWatch,
} from './lib/maintenanceMode'
import { initializeErrorTracking, posthog } from './lib/posthog'
import { registerDictationConsoleHelpers } from './lib/speechRecognition'
import { useAppStore } from './store/app'
import { useAuthStore } from './store/auth'
import { isEmbedRuntime, isFramed } from './utils/embed'
import { isJwtExpired } from './utils/jwt'
import { appAbsoluteUrl } from './utils/publicGpt'

import type { KeycloakConfig, KeycloakInitOptions } from 'keycloak-js'

const logger = createLogger('main')

/**
 * Embedded mode: the public GPT page rendered inside ANY frame — the widget bubble
 * (which marks itself with `?embed=1`) and the flagless `<iframe>` snippet admins
 * paste alike. Changes auth bootstrapping: silently pick up the existing Foundever
 * session instead of redirecting to the login page inside the frame.
 *
 * Framing, not the flag, is the condition — the flag exists only so the widget can be
 * exercised outside a real frame. Keyed on the flag alone, a pasted iframe got no
 * `check-sso`, so `keycloak.init` reported "not authenticated" for a visitor holding a
 * perfectly good Keycloak session (it never looked), and bootstrap then sent the frame
 * to a Keycloak login page that refuses to be framed. Existing embeds are already
 * deployed and can't be re-pasted, so the app has to recognise them on its own.
 *
 * Still gated on the public GPT path so that framing another
 * `allowWithoutEverGptAccess` page (/w, /share) can't spin up the silent-SSO/host-token
 * bridge outside the surface it was built for.
 */
const isEmbed =
  (isEmbedRuntime() || isFramed()) &&
  window.location.pathname.includes('/public/gpt/')

const keycloakOptions: KeycloakConfig = {
  url: import.meta.env.VITE_KEYCLOAK_URL,
  realm: import.meta.env.VITE_KEYCLOAK_REALM,
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
}

export const keycloak = new Keycloak(keycloakOptions)

/** OAuth redirect after login; dev uses the live origin so preview/develop builds in `.env` cannot override localhost. */
export const keycloakRedirectUri = import.meta.env.DEV
  ? `${window.location.origin}/`
  : import.meta.env.VITE_KEYCLOAK_LOGIN_URL

/**
 * Absolute URL of the silent-SSO callback (`public/silent-check-sso.html`), on the
 * app's own origin and honouring any deploy base path. Must be registered as a valid
 * redirect URI on the Keycloak client for embed-mode silent SSO to succeed.
 */
const silentCheckSsoRedirectUri = (): string =>
  appAbsoluteUrl('silent-check-sso.html')

const { accessToken, refreshToken, idToken } = useAuthTokens()

const authenticateKeycloakUser = () => {
  useAuthStore().login({
    _accessToken: keycloak.token ?? '',
    _refreshToken: keycloak.refreshToken ?? '',
    _idToken: keycloak.idToken ?? '',
    _sessionState: keycloak.sessionId ?? '',
  })

  // Don't fetch user info if user store already has user data
  if (useAuthStore().user) {
    return Promise.resolve()
  }
  return fetchUserInfo()
}
const fetchUserInfo = () =>
  AuthApi.me()
    .then((user) => {
      useAuthStore().setUser(user)
      // Identify posthog user
      posthog.identify(user.id, {
        employee_id: user.employee_id,
      })
    })
    .catch((err) => {
      console.error('[auth.me] error', err)
    })

// Recover automatically when a lazy chunk (icons, routes, …) 404s after a deploy
// instead of leaving blank icons that only a manual hard refresh would fix.
registerChunkErrorRecovery()
registerDictationConsoleHelpers()

const app = createApp(App)

initializeErrorTracking(app)

// Maintenance mode: the `maintenance-page` flag swaps the shell for the maintenance
// screen (App.vue), in place of the old hard redirect to the static `/500.html` —
// same instant switch, but the in-app screen clears itself once the flag goes off.
// Bootstrap below still runs behind it; only the login redirect is suppressed.
startMaintenanceModeWatch()

app.use(store)
schedulePwaClientInit()
app.use(router).use(VueQueryPlugin, { queryClient }).mount('#app')

const keycloakInitOptions: KeycloakInitOptions = {
  checkLoginIframe: true,
  redirectUri: keycloakRedirectUri,
  token: accessToken.value,
  refreshToken: refreshToken.value,
  idToken: idToken.value,
  // Embed mode: silently adopt the visitor's existing Foundever session (works
  // because host, app, and Keycloak are all same-site under foundever.com) instead
  // of forcing a login redirect that a bubble-sized iframe can't render.
  ...(isEmbed
    ? {
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: silentCheckSsoRedirectUri(),
      }
    : {}),
}

/**
 * `keycloak.init` only resolves once the `checkLoginIframe` handshake completes, and
 * that handshake can hang indefinitely (blocked third-party cookies, an iframe load
 * that never lands). Nothing downstream is guarded against that: `setIsAppLoaded`
 * would never run, so the app would sit behind its page loader forever instead of
 * ever rendering. Bounding it degrades cross-tab session *monitoring* only — the
 * stored token still authenticates the user, and per-request refresh still runs.
 */
const KEYCLOAK_INIT_TIMEOUT_MS = 15_000

/**
 * Bootstrap must always end with the shell revealed. `keycloak.init` is bounded
 * below, but the steps after it — the pre-auth token refresh and `/users/me` — are
 * network calls that can stall too, and until `setIsAppLoaded` runs the app is
 * nothing but a page loader with no way out except a reload. So reveal it regardless
 * once this elapses and let the route guards decide what the user sees.
 */
const BOOTSTRAP_FAILSAFE_MS = 30_000

const bootstrapFailsafeTimer = window.setTimeout(() => {
  logger.warn(
    `Bootstrap did not finish in ${BOOTSTRAP_FAILSAFE_MS}ms — revealing the app shell`,
  )
  useAppStore().setIsAppLoaded(true)
}, BOOTSTRAP_FAILSAFE_MS)

const revealAppShell = () => {
  window.clearTimeout(bootstrapFailsafeTimer)
  useAppStore().setIsAppLoaded(true)
}

const initKeycloakWithinTimeout = (): Promise<boolean> =>
  new Promise((resolve, reject) => {
    let isSettled = false
    let timer = 0
    const settle = (run: () => void) => {
      if (isSettled) return
      isSettled = true
      window.clearTimeout(timer)
      run()
    }

    timer = window.setTimeout(() => {
      settle(() => {
        logger.warn(
          `keycloak.init did not settle in ${KEYCLOAK_INIT_TIMEOUT_MS}ms — continuing with the stored token`,
        )
        // Treat a stored access token as signed in — but only while it is still
        // valid. `refreshKeycloakToken` goes through the adapter that just failed to
        // settle, so an expired token can't be renewed here: every request would go
        // out with it and 401, which reaches the user as "this GPT could not be
        // loaded" rather than the sign-in they actually need. Without a usable token
        // there is nothing to continue with and the unauthenticated path runs.
        resolve(Boolean(accessToken.value) && !isJwtExpired(accessToken.value))
      })
    }, KEYCLOAK_INIT_TIMEOUT_MS)

    keycloak.init(keycloakInitOptions).then(
      (isAuthenticated) => settle(() => resolve(isAuthenticated)),
      (error: unknown) => settle(() => reject(error)),
    )
  })

initKeycloakWithinTimeout()
  .then(async (isAuthenticated) => {
    logger.log('isAuthenticated', isAuthenticated)
    if (isAuthenticated) {
      logger.log('Refreshing token before auth...')
      await refreshKeycloakToken()
      logger.log('Authenticating keycloak user...')
      await authenticateKeycloakUser()
      // Identify posthog user
      const userId = useAuthStore().user?.id
      posthog.identify(userId, {
        employee_id: useAuthStore().user?.employee_id,
      })
      // Pageviews count identified users only, auto-capture is off.
      if (userId) posthog.capture('$pageview')
      trackStandalonePwaInstall()
    } else if (isEmbed) {
      // Silent SSO found no session — fall back to a host-provided token rather than
      // bouncing to login inside the iframe.
      logger.log('Embed mode without a session, awaiting host token…')
      initEmbedAuthBridge()
    } else {
      logger.log('Not authenticated, logging out...')
      useAuthStore().logout(true)
    }

    revealAppShell()
  })
  .catch((error: unknown) => {
    logger.log('Error during initialisation', error)
    if (isEmbed) {
      // Silent SSO iframe failed (e.g. blocked) — try the host-token fallback and
      // let the embedded page show its own state, never a login redirect.
      initEmbedAuthBridge()
    } else {
      setPostAuthRedirect(window.location.pathname + window.location.search)
      useAuthStore().logout(true)
    }
    // Forced same-URL navigation in `setIsAppLoaded` runs `authGuard` → Login.
    revealAppShell()
  })

// Handle auth success
keycloak.onAuthSuccess = () => {
  accessToken.value = keycloak.token
  refreshToken.value = keycloak.refreshToken
}
// Handle expired tokens
keycloak.onTokenExpired = () => {
  void refreshKeycloakToken()
}

/** Through Keycloak and back to this page: silent while the SSO session is alive. */
const signInAgain = () => {
  // Never during maintenance: a maintenance window is when Keycloak is as likely
  // to be down as the API, so this redirect would replace the screen explaining
  // the outage with a broken login page the user can't get back from.
  if (isMaintenanceMode.value) return
  // Keycloak's login page cannot render in a frame; an anonymous page needs no session.
  if (isFramed()) return
  if (router.resolve(window.location.pathname).meta.allowAnonymous) return

  setPostAuthRedirect(window.location.pathname + window.location.search)
  void keycloak.login()
}

/** `force` renews a token the API has just rejected, even if it still looks valid. */
export const refreshKeycloakToken = ({ force = false } = {}) => {
  if (!keycloak.refreshToken) {
    // The adapter drops its tokens when the Keycloak session ends; the stored one outlives it.
    if (force && accessToken.value && isJwtExpired(accessToken.value)) {
      signInAgain()
      return Promise.reject(new Error(SESSION_EXPIRED_USER_MESSAGE))
    }
    return Promise.resolve()
  }

  // `updateToken` goes through the Keycloak adapter and can sit unresolved when
  // `init` never settled (see `initKeycloakWithinTimeout`). The API client awaits
  // this on every request, so calling it in that state would hang the whole app
  // rather than just the refresh. The stored token still authorizes requests.
  if (!keycloak.didInitialize) {
    return Promise.resolve()
  }

  return keycloak
    .updateToken(force ? -1 : undefined)
    .then(() => {
      localStorage.setItem('token_refreshed_at', Date.now().toString())
      accessToken.value = keycloak.token
      refreshToken.value = keycloak.refreshToken
    })
    .catch((err) => {
      signInAgain()
      throw err
    })
}
